import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { 
  Sparkles, TrendingDown, AlertTriangle, CheckCircle2, XCircle,
  RefreshCw, Play, Eye, ThumbsUp, ThumbsDown, Lightbulb, 
  Target, Clock, Users, FileText, Brain, Zap
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-green-500'
};

const CATEGORY_COLORS = {
  clinical: 'text-emerald-500',
  safety: 'text-orange-500',
  regulatory: 'text-purple-500',
  staff: 'text-blue-500',
  patient_experience: 'text-pink-500'
};

const TrendMiniChart = ({ data, direction }) => {
  if (!data || data.length < 2) return null;
  
  const chartData = data.map((value, idx) => ({ period: idx + 1, value }));
  const color = direction === 'higher' 
    ? (data[data.length - 1] < data[0] ? '#ef4444' : '#22c55e')
    : (data[data.length - 1] > data[0] ? '#ef4444' : '#22c55e');

  return (
    <div className="h-16 w-32">
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const AutoPIPCard = ({ pip, onView, onApprove, onReject }) => {
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <div className={`h-1 ${PRIORITY_COLORS[pip.ai_priority] || 'bg-gray-500'}`} />
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className={CATEGORY_COLORS[pip.category]}>
                {pip.category.replace('_', ' ')}
              </Badge>
              <Badge className={PRIORITY_COLORS[pip.ai_priority]}>
                {pip.ai_priority}
              </Badge>
              {pip.trigger_reason === 'both' && (
                <Badge variant="destructive">⚠️ Multiple Triggers</Badge>
              )}
            </div>
            
            <h3 className="font-semibold text-lg mb-1">{pip.metric_display_name}</h3>
            
            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
              <span className="flex items-center gap-1">
                <Target className="h-4 w-4" />
                Current: {pip.current_value}{pip.metric_name.includes('percent') ? '%' : ''}
              </span>
              <span className="flex items-center gap-1">
                <Target className="h-4 w-4 text-green-500" />
                Target: {pip.target_value}{pip.metric_name.includes('percent') ? '%' : ''}
              </span>
            </div>
            
            {pip.ai_problem_statement && (
              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                {pip.ai_problem_statement}
              </p>
            )}
            
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {pip.decline_periods > 0 && (
                <span className="flex items-center gap-1 text-red-500">
                  <TrendingDown className="h-3 w-3" />
                  {pip.decline_periods} periods declining
                </span>
              )}
              {pip.below_target_percent > 0 && (
                <span className="flex items-center gap-1 text-orange-500">
                  <AlertTriangle className="h-3 w-3" />
                  {pip.below_target_percent.toFixed(1)}% below target
                </span>
              )}
            </div>
          </div>
          
          <div className="flex flex-col items-end gap-2">
            <TrendMiniChart 
              data={pip.trend_data} 
              direction={pip.metric_name.includes('percent') && !pip.metric_name.includes('turnover') ? 'higher' : 'lower'} 
            />
            
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => onView(pip)}>
                <Eye className="h-4 w-4" />
              </Button>
              <Button variant="default" size="sm" onClick={() => onApprove(pip)}>
                <ThumbsUp className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onReject(pip)}>
                <ThumbsDown className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function AutoPIP() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pendingPIPs, setPendingPIPs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selectedPIP, setSelectedPIP] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approvalData, setApprovalData] = useState({ title: '', notes: '' });

  const fetchPendingPIPs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auto-pip/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingPIPs(data.pending_pips || []);
      }
    } catch (error) {
      console.error('Failed to fetch pending PIPs');
    }
  };

  const analyzeMetrics = async () => {
    setAnalyzing(true);
    try {
      const response = await fetch(`${API_URL}/api/auto-pip/analyze`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCandidates(data.candidates || []);
        toast.success(`Analysis complete`, {
          description: `Found ${data.total_candidates} metrics needing attention`
        });
      }
    } catch (error) {
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const generatePIPs = async () => {
    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/auto-pip/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Generated ${data.total_generated} Auto-PIPs`, {
          description: 'AI guidance has been added to each PIP'
        });
        fetchPendingPIPs();
        setCandidates([]);
      }
    } catch (error) {
      toast.error('Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const viewPIPDetails = async (pip) => {
    try {
      const response = await fetch(`${API_URL}/api/auto-pip/${pip.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSelectedPIP(data);
        setShowDetailDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load details');
    }
  };

  const startApproval = (pip) => {
    setSelectedPIP(pip);
    setApprovalData({
      title: `Improve ${pip.metric_display_name}`,
      notes: ''
    });
    setShowApproveDialog(true);
  };

  const approvePIP = async () => {
    if (!selectedPIP) return;
    
    try {
      const response = await fetch(`${API_URL}/api/auto-pip/${selectedPIP.id}/approve`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: approvalData.title,
          notes: approvalData.notes
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('PIP Created', {
          description: `${data.pip_title} is now active`
        });
        setShowApproveDialog(false);
        fetchPendingPIPs();
      } else {
        toast.error('Approval failed');
      }
    } catch (error) {
      toast.error('Approval failed');
    }
  };

  const rejectPIP = async (pip) => {
    try {
      const response = await fetch(`${API_URL}/api/auto-pip/${pip.id}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Auto-PIP rejected');
        fetchPendingPIPs();
      }
    } catch (error) {
      toast.error('Rejection failed');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchPendingPIPs();
      setLoading(false);
    };
    loadData();
  }, [token]);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="auto-pip-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="auto-pip-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Sparkles className="h-8 w-8 text-primary" />
            Automated PIP Generation
          </h1>
          <p className="text-muted-foreground mt-1">
            AI-powered Performance Improvement Projects based on trending data
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={analyzeMetrics} disabled={analyzing}>
            {analyzing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Brain className="h-4 w-4 mr-2" />
                Analyze Trends
              </>
            )}
          </Button>
          {candidates.length > 0 && (
            <Button onClick={generatePIPs} disabled={generating}>
              {generating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4 mr-2" />
                  Generate {candidates.length} PIPs
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-3xl font-bold">{pendingPIPs.length}</p>
              </div>
              <FileText className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Candidates Found</p>
                <p className="text-3xl font-bold">{candidates.length}</p>
              </div>
              <TrendingDown className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Critical Priority</p>
                <p className="text-3xl font-bold">
                  {pendingPIPs.filter(p => p.ai_priority === 'critical').length}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">AI Guidance</p>
                <p className="text-3xl font-bold">
                  {pendingPIPs.filter(p => p.ai_problem_statement).length}
                </p>
              </div>
              <Lightbulb className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Analysis Results */}
      {candidates.length > 0 && (
        <Alert className="border-orange-500/50 bg-orange-500/5">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          <AlertTitle>Metrics Requiring Attention</AlertTitle>
          <AlertDescription>
            {candidates.length} metrics detected with declining trends or below-target performance. 
            Click "Generate PIPs" to create AI-powered improvement plans.
          </AlertDescription>
        </Alert>
      )}

      {/* Pending PIPs */}
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">
            Pending Review ({pendingPIPs.length})
          </TabsTrigger>
          <TabsTrigger value="candidates" disabled={candidates.length === 0}>
            Analysis Results ({candidates.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          {pendingPIPs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Sparkles className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No Pending Auto-PIPs</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Click "Analyze Trends" to scan metrics for declining performance
                </p>
                <Button variant="outline" onClick={analyzeMetrics} disabled={analyzing}>
                  <Brain className="h-4 w-4 mr-2" />
                  Analyze Now
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {pendingPIPs.map((pip) => (
                <AutoPIPCard
                  key={pip.id}
                  pip={pip}
                  onView={viewPIPDetails}
                  onApprove={startApproval}
                  onReject={rejectPIP}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="candidates" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {candidates.map((candidate, idx) => (
              <Card key={idx}>
                <CardContent className="pt-4">
                  <Badge variant="outline" className={CATEGORY_COLORS[candidate.category]}>
                    {candidate.category.replace('_', ' ')}
                  </Badge>
                  <h3 className="font-medium mt-2">{candidate.metric_display_name}</h3>
                  <div className="flex items-center gap-4 mt-2 text-sm">
                    <span>Current: {candidate.current_value}</span>
                    <span className="text-green-500">Target: {candidate.target_value}</span>
                  </div>
                  <Badge variant="destructive" className="mt-2">
                    {candidate.trigger_reason.replace('_', ' ')}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              {selectedPIP?.metric_display_name}
            </DialogTitle>
            <DialogDescription>
              AI-Generated Performance Improvement Plan
            </DialogDescription>
          </DialogHeader>

          {selectedPIP && (
            <div className="space-y-6">
              {/* Problem Statement */}
              <div>
                <h4 className="font-medium flex items-center gap-2 mb-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500" />
                  Problem Statement
                </h4>
                <p className="text-sm text-muted-foreground bg-muted p-3 rounded-lg">
                  {selectedPIP.ai_problem_statement || 'No AI analysis available'}
                </p>
              </div>

              {/* Root Cause Analysis */}
              {selectedPIP.ai_root_cause_analysis?.length > 0 && (
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Brain className="h-4 w-4 text-purple-500" />
                    Potential Root Causes
                  </h4>
                  <ul className="space-y-2">
                    {selectedPIP.ai_root_cause_analysis.map((cause, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-purple-500">•</span>
                        {cause}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Implementation Plan */}
              {selectedPIP.ai_implementation_plan?.length > 0 && (
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-blue-500" />
                    Implementation Timeline ({selectedPIP.ai_timeline_weeks} weeks)
                  </h4>
                  <div className="space-y-3">
                    {selectedPIP.ai_implementation_plan.map((phase, idx) => (
                      <div key={idx} className="border rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="outline">Week {phase.week}</Badge>
                          <span className="text-sm font-medium">{phase.phase}</span>
                        </div>
                        <ul className="text-sm text-muted-foreground space-y-1">
                          {phase.actions?.map((action, aIdx) => (
                            <li key={aIdx}>• {action}</li>
                          ))}
                        </ul>
                        {phase.responsible && (
                          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                            <Users className="h-3 w-3" /> {phase.responsible}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Best Practices */}
              {selectedPIP.ai_best_practices?.length > 0 && (
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Lightbulb className="h-4 w-4 text-yellow-500" />
                    Best Practices
                  </h4>
                  <ul className="space-y-2">
                    {selectedPIP.ai_best_practices.map((practice, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                        {practice}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Success Metrics */}
              {selectedPIP.ai_success_metrics?.length > 0 && (
                <div>
                  <h4 className="font-medium flex items-center gap-2 mb-2">
                    <Target className="h-4 w-4 text-green-500" />
                    Success Metrics
                  </h4>
                  <ul className="space-y-2">
                    {selectedPIP.ai_success_metrics.map((metric, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-green-500">✓</span>
                        {metric}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Close
            </Button>
            <Button onClick={() => { setShowDetailDialog(false); startApproval(selectedPIP); }}>
              <ThumbsUp className="h-4 w-4 mr-2" />
              Approve & Create PIP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create PIP from Auto-Analysis</DialogTitle>
            <DialogDescription>
              Review and customize the PIP details before creating
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>PIP Title</Label>
              <Input
                value={approvalData.title}
                onChange={(e) => setApprovalData({ ...approvalData, title: e.target.value })}
                placeholder="Enter PIP title"
              />
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={approvalData.notes}
                onChange={(e) => setApprovalData({ ...approvalData, notes: e.target.value })}
                placeholder="Add any additional notes..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button onClick={approvePIP}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Create PIP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
